/**
 * Liest ein Feld aus einem Bus-Subject, unabhaengig von seiner FORM.
 *
 * ⚠️⚠️ Es sind drei Formen im Umlauf, und ein Leser, der nur eine kennt,
 * bekommt `undefined` statt eines Fehlers — er verwirft das Event **still**:
 *
 *   1. flach              `{ external_user_id: "…" }`
 *   2. unter `subject`    `{ subject: { external_user_id: "…" } }`
 *   3. kanonisch (`Keys`) `{ Tenant, Resource, Keys: { external_user_id: "…" } }`
 *   4. Hub-Huelle         `{ event_key, subject: { Keys: {…}, Parents: [ { Keys: {…} } ] } }`
 *
 * Form 3 ist das Ziel (PFS-1408). Die Sender wandern nacheinander dorthin;
 * bis alle um sind — und bis die Queues durchgelaufen sind — muessen 1 und 2
 * lesbar bleiben.
 *
 * ⭐⭐ Form 4 ist das, was der Browser WIRKLICH bekommt (PFS-1484): der SSE-Hub
 * schickt den ganzen Envelope, das Subject steckt unter `subject`. Und seit
 * v0.77.14 liegt ein Feld, das der Eltern-Ressource gehoert (z.B.
 * `external_user_id` bei einem `profile`/`settings`-Event), NUR noch in
 * `Parents[*].Keys` — nicht mehr in den eigenen `Keys`. Reihenfolge: eigene
 * Formen vor Eltern, erster Treffer gewinnt.
 *
 * ⭐ Warum das hier liegt und nicht je Komponente: genau die Doppelung war der
 * Befund. Wo zwei Formen existieren, schreibt sonst jeder Leser seine eigene
 * Kette — und eine davon vergisst einen Zweig. So geschehen in
 * `pfs-notification-bell` und `mednet-identity`: beide kannten `subject`,
 * keiner `Keys`.
 */
export function subjectField(
  value: unknown,
  field: string,
): string | undefined {
  const rec = asRecord(value);
  const own = fromSubjectShape(rec, field);
  if (own) return own;
  return fromSubjectShape(asRecord(rec["subject"]), field);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

// fromSubjectShape liest ein Feld aus EINEM Subject: flach, dann Keys, dann
// jede Eltern-Ressource (Parents[*].Keys).
function fromSubjectShape(
  subject: Record<string, unknown>,
  field: string,
): string | undefined {
  const flat = str(subject[field]);
  if (flat) return flat;
  const fromKeys = str(asRecord(subject["Keys"])[field]);
  if (fromKeys) return fromKeys;
  const parents = subject["Parents"];
  if (Array.isArray(parents)) {
    for (const parent of parents) {
      const fromParent = str(asRecord(asRecord(parent)["Keys"])[field]);
      if (fromParent) return fromParent;
    }
  }
  return undefined;
}
